import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Package, Star, ChevronLeft, ChevronRight } from 'lucide-react';
import { packsApiService, Pack } from '../services/packsApi';

const PacksCarousel: React.FC = () => {
  const navigate = useNavigate();
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isManualControl, setIsManualControl] = useState(false);
  const carouselRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    loadPacks();
  }, []);

  const loadPacks = async () => {
    try {
      // Récupérer les packs actifs depuis l'API
      const packsData = await packsApiService.getPacks(true);
      setPacks(packsData);
    } catch (error) {
      console.error('Erreur lors du chargement des packs:', error);
      // En cas d'erreur, afficher un tableau vide
      setPacks([]);
    } finally {
      setLoading(false);
    }
  };

  const truncateText = (text: string, maxLength: number = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const nextSlide = () => {
    setIsManualControl(true);
    setCurrentIndex((prev) => (prev + 1) % packs.length);
    setTimeout(() => setIsManualControl(false), 3000); // Reprendre l'auto-scroll après 3s
  };

  const prevSlide = () => {
    setIsManualControl(true);
    setCurrentIndex((prev) => (prev - 1 + packs.length) % packs.length);
    setTimeout(() => setIsManualControl(false), 3000); // Reprendre l'auto-scroll après 3s
  };

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (packs.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        Aucun pack disponible pour le moment
      </div>
    );
  }

  return (
    <div className="relative overflow-hidden">
      {/* Boutons de navigation */}
      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-white/50 flex items-center justify-center hover:bg-white hover:shadow-xl transition-all duration-300 group"
      >
        <ChevronLeft className="w-6 h-6 text-gray-700 group-hover:text-purple-600" />
      </button>
      
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 transform -translate-y-1/2 z-20 w-12 h-12 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-white/50 flex items-center justify-center hover:bg-white hover:shadow-xl transition-all duration-300 group"
      >
        <ChevronRight className="w-6 h-6 text-gray-700 group-hover:text-purple-600" />
      </button>

      {/* Carrousel en défilement continu */}
      <div 
        ref={carouselRef}
        className={`flex space-x-6 transition-transform duration-500 ${
          isManualControl ? '' : 'animate-scroll-right'
        }`}
        style={{
          transform: isManualControl ? `translateX(-${currentIndex * 336}px)` : undefined
        }}
      >
        {/* Première série */}
        {packs.map((pack, index) => (
          <motion.div
            key={`first-${pack.id}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="flex-shrink-0 w-80 rounded-2xl p-6 shadow-lg border transition-all duration-300 group hover:shadow-xl bg-white/80 backdrop-blur-sm border-white/50"
          >
            <div className="relative mb-4">
              
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-r from-blue-500 to-purple-600">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                  {pack.name}
                </h3>
              </div>
              
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                {truncateText(pack.description)}
              </p>
              
              {pack.services && pack.services.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Services inclus :</p>
                  <div className="flex flex-wrap gap-1">
                    {pack.services.map((service, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-3 py-1 rounded-full font-medium bg-blue-100 text-blue-700"
                      >
                        {service.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-blue-600">
                  {pack.total_price.toLocaleString()} FCFA
                </span>
              </div>
              
              <button
                onClick={() => navigate('/packs')}
                className="flex items-center space-x-2 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all duration-300 group-hover:scale-105 bg-gradient-to-r from-blue-600 to-purple-600"
              >
                <span className="text-sm font-medium">En savoir plus</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
        
        {/* Deuxième série pour le défilement continu */}
        {packs.map((pack, index) => (
          <motion.div
            key={`second-${pack.id}`}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: (packs.length + index) * 0.1 }}
            className="flex-shrink-0 w-80 rounded-2xl p-6 shadow-lg border transition-all duration-300 group hover:shadow-xl bg-white/80 backdrop-blur-sm border-white/50"
          >
            <div className="relative mb-4">
              
              <div className="flex items-center space-x-3 mb-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-r from-blue-500 to-purple-600">
                  <Package className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                  {pack.name}
                </h3>
              </div>
              
              <p className="text-gray-700 text-sm leading-relaxed mb-4">
                {truncateText(pack.description)}
              </p>
              
              {pack.services && pack.services.length > 0 && (
                <div className="mb-4">
                  <p className="text-xs font-semibold text-gray-700 mb-2">Services inclus :</p>
                  <div className="flex flex-wrap gap-1">
                    {pack.services.map((service, idx) => (
                      <span 
                        key={idx}
                        className="text-xs px-3 py-1 rounded-full font-medium bg-blue-100 text-blue-700"
                      >
                        {service.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                <span className="font-semibold text-blue-600">
                  {pack.total_price.toLocaleString()} FCFA
                </span>
              </div>
              
              <button
                onClick={() => navigate('/packs')}
                className="flex items-center space-x-2 text-white px-4 py-2 rounded-lg hover:shadow-lg transition-all duration-300 group-hover:scale-105 bg-gradient-to-r from-blue-600 to-purple-600"
              >
                <span className="text-sm font-medium">En savoir plus</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        ))}
      </div>
      
      {/* Gradients de masquage */}
      <div className="absolute top-0 left-0 w-20 h-full bg-gradient-to-r from-slate-50 to-transparent pointer-events-none z-10"></div>
      <div className="absolute top-0 right-0 w-20 h-full bg-gradient-to-l from-slate-50 to-transparent pointer-events-none z-10"></div>
      
      {/* Indicateurs de position */}
      <div className="flex justify-center mt-6 space-x-2">
        {packs.map((_, index) => (
          <button
            key={index}
            onClick={() => {
              setIsManualControl(true);
              setCurrentIndex(index);
              setTimeout(() => setIsManualControl(false), 3000);
            }}
            className={`w-2 h-2 rounded-full transition-all duration-300 ${
              index === currentIndex % packs.length
                ? 'bg-purple-600 w-6'
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default PacksCarousel;
